import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { useToasts } from '../context/ToastContext';

const AddEducationRecordModal = ({ isOpen, onClose, onSubmit, educationType }) => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [type, setType] = useState(educationType || '三级教育');
  const [content, setContent] = useState('');
  const [instructor, setInstructor] = useState('');
  const [examiner, setExaminer] = useState('');
  const [trainingDuration, setTrainingDuration] = useState('');
  const [examScore, setExamScore] = useState('');
  const [examResult, setExamResult] = useState('合格');
  const [companyNotes, setCompanyNotes] = useState('');
  const [departmentNotes, setDepartmentNotes] = useState('');
  const [teamNotes, setTeamNotes] = useState('');
  const [activeNoteTab, setActiveNoteTab] = useState('company');
  const [files, setFiles] = useState([]); // Changed to handle multiple files
  const { addToast } = useToasts();

  useEffect(() => {
    if (educationType) {
      setType(educationType);
    }
  }, [educationType]);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDate(new Date().toISOString().split('T')[0]);
      setContent('');
      setInstructor('');
      setExaminer('');
      setTrainingDuration('');
      setExamScore('');
      setExamResult('合格');
      setCompanyNotes('');
      setDepartmentNotes('');
      setTeamNotes('');
      setActiveNoteTab('company');
      setFiles([]); // Reset files array
    }
  }, [isOpen]);

  useEffect(() => {
    if (type !== '三级教育') {
      setInstructor('');
      setExaminer('');
      setTrainingDuration('');
      setExamScore('');
      setExamResult('');
      setCompanyNotes('');
      setDepartmentNotes('');
      setTeamNotes('');
      setActiveNoteTab('company');
    } else {
      setExamResult(prev => (prev ? prev : '合格'));
    }
  }, [type]);

  const handleFileChange = (e) => {
    // Append new files to the existing list, avoiding duplicates
    const newFiles = Array.from(e.target.files);
    // Client-side file size validation: warn and skip files that exceed 20MB
    const MAX_CLIENT_FILE_SIZE = 20 * 1024 * 1024; // 20MB
    const oversized = newFiles.filter(f => f.size > MAX_CLIENT_FILE_SIZE);
    if (oversized.length > 0) {
      addToast(`有 ${oversized.length} 个文件超过 20MB，已跳过这些文件。建议压缩或拆分文件后重试。`, 'warning');
    }
    const filteredFiles = newFiles.filter(f => f.size <= MAX_CLIENT_FILE_SIZE);
    setFiles(prevFiles => {
      const existingFileNames = new Set(prevFiles.map(f => f.name));
      const uniqueNewFiles = filteredFiles.filter(f => !existingFileNames.has(f.name));
      return [...prevFiles, ...uniqueNewFiles];
    });
    // Reset the input value to allow selecting the same file again after removing it
    e.target.value = null; 
  };

  const handleRemoveFile = (fileName) => {
    setFiles(files.filter(file => file.name !== fileName));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title || !date || !type) {
      addToast('请填写所有必填项：标题、日期和类型。', 'warning');
      return;
    }
    const recordData = {
      title,
      education_type: type,
      education_date: date,
      content,
    };

    if (type === '三级教育') {
      Object.assign(recordData, {
        instructor,
        examiner,
        training_duration: trainingDuration,
        exam_score: examScore,
        exam_result: examResult,
        company_notes: companyNotes,
        department_notes: departmentNotes,
        team_notes: teamNotes
      });
    }

    onSubmit(recordData, files); // Pass files array
  };

  // --- Styles for preview ---
  const styles = {
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem'
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.45rem'
    },
    inlineRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '1rem'
    },
    wideInlineRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '1rem'
    },
    label: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: 'var(--text-primary)'
    },
    control: {
      padding: '0.75rem 0.9rem',
      borderRadius: '12px',
      border: '1px solid rgba(148,163,184,0.35)',
      background: 'var(--bg-primary)',
      fontSize: '0.95rem',
      color: 'var(--text-primary)',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    },
    textarea: {
      padding: '0.9rem 1rem',
      borderRadius: '12px',
      border: '1px solid rgba(148,163,184,0.35)',
      background: 'var(--bg-primary)',
      fontSize: '0.95rem',
      color: 'var(--text-primary)',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      minHeight: '140px',
      resize: 'vertical'
    },
    filePicker: {
      padding: '0.9rem 1rem',
      borderRadius: '12px',
      border: '1px dashed rgba(148,163,184,0.45)',
      background: 'rgba(29,118,252,0.05)',
      fontSize: '0.9rem',
      cursor: 'pointer'
    },
    helperText: {
      fontSize: '0.8rem',
      color: 'var(--text-secondary)'
    },
    noteTabsWrapper: {
      marginTop: '0.5rem',
      border: '1px solid rgba(148,163,184,0.32)',
      borderRadius: '12px',
      background: 'var(--bg-secondary)',
      overflow: 'hidden'
    },
    noteTabBar: {
      display: 'flex',
      gap: '0.5rem',
      padding: '0.75rem 0.85rem',
      background: 'rgba(148,163,184,0.16)',
      flexWrap: 'wrap'
    },
    noteTabButton: {
      border: 'none',
      background: 'transparent',
      color: 'var(--text-secondary)',
      padding: '0.5rem 1rem',
      borderRadius: '999px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      transition: 'all 0.2s ease'
    },
    noteTabButtonActive: {
      background: 'rgba(29,118,252,0.15)',
      color: 'var(--brand-primary)',
      boxShadow: '0 12px 24px rgba(29,118,252,0.18)'
    },
    noteTabPanel: {
      padding: '1rem 1.2rem',
      background: 'var(--bg-primary)'
    },
    previewContainer: {
      marginTop: '0.5rem',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    previewItem: {
      position: 'relative',
      width: '104px',
      height: '104px',
      border: '1px solid rgba(148,163,184,0.35)',
      borderRadius: '12px',
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'var(--bg-secondary)',
      boxShadow: '0 10px 22px rgba(15,23,42,0.08)'
    },
    previewImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    removeButton: {
      position: 'absolute',
      top: '6px',
      right: '6px',
      width: '22px',
      height: '22px',
      borderRadius: '50%',
      background: 'rgba(30, 41, 59, 0.78)',
      color: '#fff',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '0.85rem',
      lineHeight: 1
    },
    fileInfo: {
      padding: '0.65rem',
      fontSize: '0.7rem',
      textAlign: 'center',
      wordBreak: 'break-word'
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.85rem',
      marginTop: '1.5rem'
    },
    secondaryButton: {
      border: '1px solid rgba(148,163,184,0.5)',
      background: 'var(--bg-secondary)',
      color: 'var(--text-secondary)',
      padding: '0.6rem 1.2rem',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontWeight: 500
    },
    primaryButton: {
      border: 'none',
      background: 'linear-gradient(135deg, var(--brand-primary), #5b7bff)',
      color: '#fff',
      padding: '0.7rem 1.6rem',
      borderRadius: '999px',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 18px 32px rgba(29,118,252,0.22)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease'
    },
    primaryButtonIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.5rem',
      height: '1.5rem',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.22)',
      fontSize: '0.95rem',
      lineHeight: 1
    }
  };

  const isLevelThree = type === '三级教育';

  const noteTabs = [
    {
      key: 'company',
      label: '公司级教育记录',
      value: companyNotes,
      onChange: setCompanyNotes,
      placeholder: '记录公司级教育的授课要点、互动情况等'
    },
    {
      key: 'department',
      label: '厂（部）级教育记录',
      value: departmentNotes,
      onChange: setDepartmentNotes,
      placeholder: '记录厂（部）级教育内容，如规章制度、安全警示等'
    },
    {
      key: 'team',
      label: '班组级教育记录',
      value: teamNotes,
      onChange: setTeamNotes,
      placeholder: '记录班组级教育内容，如现场交底、实操训练等'
    },
    {
      key: 'summary',
      label: '总结与备注',
      value: content,
      onChange: setContent,
      placeholder: '补充培训效果评价、注意事项等信息'
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isLevelThree ? '填写三级安全教育登记卡' : '添加新的教育记录'}
      maxWidth={isLevelThree ? '920px' : '720px'}
      bodyStyle={{ maxHeight: isLevelThree ? '70vh' : '75vh' }}
    >
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.inlineRow}>
          <div style={styles.fieldGroup}>
            <label htmlFor="record-title" style={styles.label}>标题</label>
            <input
              id="record-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={styles.control}
              placeholder={isLevelThree ? '例如：入厂三级安全教育登记' : '请输入教育活动名称'}
              required
            />
          </div>
          <div style={styles.fieldGroup}>
            <label htmlFor="record-date" style={styles.label}>教育日期</label>
            <input
              id="record-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={styles.control}
              required
            />
          </div>
        </div>

        <div style={styles.inlineRow}>
          <div style={styles.fieldGroup}>
            <label htmlFor="record-type" style={styles.label}>教育类型</label>
            <select
              id="record-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={styles.control}
              required
              disabled={!!educationType}
            >
              <option value="三级教育">三级教育</option>
              <option value="岗前教育">岗前教育</option>
              <option value="日常教育">日常教育</option>
            </select>
          </div>
          {isLevelThree && (
            <div style={styles.fieldGroup}>
              <label htmlFor="training-duration" style={styles.label}>培训时长（小时）</label>
              <input
                id="training-duration"
                type="number"
                min="0"
                step="0.5"
                value={trainingDuration}
                onChange={(e) => setTrainingDuration(e.target.value)}
                style={styles.control}
                placeholder="例如：8"
              />
            </div>
          )}
        </div>

        {isLevelThree && (
          <>
            <div style={styles.wideInlineRow}>
              <div style={styles.fieldGroup}>
                <label htmlFor="record-instructor" style={styles.label}>培训讲师</label>
                <input
                  id="record-instructor"
                  type="text"
                  value={instructor}
                  onChange={(e) => setInstructor(e.target.value)}
                  style={styles.control}
                  placeholder="请输入讲师姓名"
                  required
                />
              </div>
              <div style={styles.fieldGroup}>
                <label htmlFor="record-examiner" style={styles.label}>考核人</label>
                <input
                  id="record-examiner"
                  type="text"
                  value={examiner}
                  onChange={(e) => setExaminer(e.target.value)}
                  style={styles.control}
                  placeholder="请输入考核人"
                  required
                />
              </div>
              <div style={styles.fieldGroup}>
                <label htmlFor="record-exam-score" style={styles.label}>考试成绩</label>
                <input
                  id="record-exam-score"
                  type="text"
                  value={examScore}
                  onChange={(e) => setExamScore(e.target.value)}
                  style={styles.control}
                  placeholder="例如：95 分"
                />
              </div>
              <div style={styles.fieldGroup}>
                <label htmlFor="record-exam-result" style={styles.label}>考试结论</label>
                <select
                  id="record-exam-result"
                  value={examResult}
                  onChange={(e) => setExamResult(e.target.value)}
                  style={styles.control}
                  required
                >
                  <option value="合格">合格</option>
                  <option value="不合格">不合格</option>
                  <option value="需复训">需复训</option>
                </select>
              </div>
            </div>

            <div style={styles.noteTabsWrapper}>
              <div style={styles.noteTabBar}>
                {noteTabs.map(tab => {
                  const isActive = activeNoteTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      style={{
                        ...styles.noteTabButton,
                        ...(isActive ? styles.noteTabButtonActive : {})
                      }}
                      onClick={() => setActiveNoteTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <div style={styles.noteTabPanel}>
                {noteTabs
                  .filter(tab => tab.key === activeNoteTab)
                  .map(tab => (
                    <textarea
                      key={tab.key}
                      value={tab.value}
                      onChange={(e) => tab.onChange(e.target.value)}
                      style={styles.textarea}
                      placeholder={tab.placeholder}
                    />
                  ))}
              </div>
            </div>
          </>
        )}

        {!isLevelThree && (
          <div style={styles.fieldGroup}>
            <label htmlFor="record-content" style={styles.label}>内容</label>
            <textarea
              id="record-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              style={styles.textarea}
              placeholder="可记录学习安排、培训时长、现场讲解重点等信息"
            ></textarea>
          </div>
        )}

        <div style={styles.fieldGroup}>
          <label htmlFor="record-attachments" style={styles.label}>附件</label>
          <label htmlFor="record-attachments" style={styles.filePicker}>
            点击选择需要上传的文件（支持多文件）
          </label>
          <input
            id="record-attachments"
            type="file"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <span style={styles.helperText}>支持图片、PDF、Office 文档等常见格式，单个文件建议不超过 20MB。</span>
        </div>

        {files.length > 0 && (
          <div style={styles.previewContainer}>
            {files.map(file => (
              <div key={file.name} style={styles.previewItem}>
                {file.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(file)} alt={file.name} style={styles.previewImage} />
                ) : (
                  <div style={styles.fileInfo}>{file.name}</div>
                )}
                <button type="button" style={styles.removeButton} onClick={() => handleRemoveFile(file.name)}>
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={styles.actions}>
          <button type="button" style={styles.secondaryButton} onClick={onClose}>取消</button>
          <button type="submit" style={styles.primaryButton}>
            <span style={styles.primaryButtonIcon}>💾</span>
            <span>保存记录</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddEducationRecordModal;
